import 'sanitize.css';
import Vue from 'vue';
import App from './App';
import logger from './plugins/logger';
import country from './plugins/country/install-vue';
import growthbook from './plugins/growthbook/growthbook-install';
import dayjs from './plugins/dayjs/install';
import { isFeatureEnabled, getCountry } from '@/plugins/country/module';
import initRouter from './router/router';
import routes from './router/routes.constants';
import store from './store/store';
import i18n, { routeLocale } from '@/i18n';
import { restoreUiLocale } from '@/i18n/uiLocale';
import VueHead from 'vue-head';
import VueSocialSharing from 'vue-social-sharing';
import { isDebug } from './utils/debug';
import { isDevMenuEnabled } from './utils/devMenu';
import { log } from './utils/logger';
import { registerServiceWorker } from './utils/registerServiceWorker';
import { initSentry } from './plugins/sentry';
import { startVersionWatch } from './utils/versionWatch';

if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

export const router = initRouter();

// Detect deploys while the tab is open, so navigations switch to the fresh
// bundle before a stale chunk request can fail. No-op in local dev (no
// release SHA baked in).
startVersionWatch();

if (isFeatureEnabled('SITEGAINER')) {
  const sitegainerScript = document.createElement('script');
  sitegainerScript.src = 'https://cdn-sitegainer.com/sitegainer_5619006.js';
  document.head.appendChild(sitegainerScript);
}

// To be able to see trustpilot widgets on release. Prod is handled in GTM.
if (isFeatureEnabled('TRUSTPILOT') && isDebug()) {
  let trustBoxScript = document.createElement('script');
  trustBoxScript.setAttribute(
    'src',
    '//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
  );
  document.head.appendChild(trustBoxScript);
}

Vue.use(logger, {
  isDebug: isDebug()
});

Vue.use(country);

// PWA: Set country-specific manifest and apple-mobile-web-app-title
(function initPwa() {
  const currentCountry = getCountry();

  const manifestLink = document.getElementById('pwa-manifest');
  if (manifestLink) {
    manifestLink.href = `/favicons/manifest-${currentCountry}.json`;
  }

  const appTitleMap = {
    se: 'Lagenhetsbyte',
    nl: 'Woningruil24',
    de: 'Wohnungsswap',
    dk: 'Hjembytte'
  };
  const titleMeta = document.createElement('meta');
  titleMeta.name = 'apple-mobile-web-app-title';
  titleMeta.content = appTitleMap[currentCountry] || appTitleMap.se;
  document.head.appendChild(titleMeta);
})();

Vue.use(dayjs);

Vue.use(VueHead, {
  separator: '-',
  complement: i18n.t('meta_title_complement', routeLocale)
});

Vue.use(VueSocialSharing);
Vue.use(growthbook);

window.isDev = isDebug();

// A language picked in the developer menu sticks while the menu is on.
restoreUiLocale(isDevMenuEnabled());

// eslint-disable-next-line
if (isDebug()) console.log('>>> Debug-mode <<<');

Vue.config.productionTip = false;
Vue.prototype.$routes = routes;

Vue.config.errorHandler = error => {
  if (error && error.stack) {
    log(`Unhandled exception: ${error.toString()} ${error.stack.toString()}`);
  } else {
    log(`Unhandled exception: ${error.toString()} `);
  }
};

window.onunhandledrejection = error => {
  if (error.reason == 'Error: Network Error') return;
  if (error.reason?.message === 'END_MATCH_CANCELLED') return;

  log(`Uncaught (in promise): ${error.reason}`);
};

window.onerror = (message, source, lineno, colno, error) => {
  if (error && error.stack) {
    log(`Js error : ${error.toString()} ${error.stack.toString()}`);
  } else if (error) {
    log(`Js error : ${error.toString()} `);
  } else {
    log(`Js error : ${message}`);
  }
};

// Initialise Sentry AFTER the app's own Vue.config.errorHandler and
// window.onerror/onunhandledrejection are assigned above. Sentry's Vue
// integration wraps whatever handler exists at init time and chains to
// it — so this ordering preserves the existing log() behaviour AND lets
// Vue/global errors reach Sentry. Initialising earlier would let these
// later assignments overwrite Sentry's wrappers, silently dropping
// errors. Still before `new Vue(...)` so the pageload transaction and
// router tracing are set up before mount. Safe no-op if no DSN.
initSentry({ Vue, router });

export const vue = new Vue({
  el: '#app',
  router,
  store,
  i18n,
  render: createElement => createElement(App)
});

registerServiceWorker();
